import React, { useState, useEffect } from "react";
import "../../styles/report.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllCustomersRequest } from "../../redux/Customers/actions/customerActions";
import {
  fetchCustomersByVendorRequest,
  fetchAllVendorsRequest
} from "../../redux/Vendors/actions/vendorActions";
import {
  fetchBookingsRequest,
  fetchCustomerBookingsRequest,
  fetchComplementaryBookingsRequest,
  fetchInstantBookingsRequest,
  resetBookingDetails
} from "../../redux/Bookings/BookingAction";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { formatDateWithAmPm, getStatusClass } from "../../Style";
import { Tabs, Tab } from "@mui/material";

const ReportTable = ({ type, customFilters = {} }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [data, setData] = useState([]);

  const [bookingType, setBookingType] = useState(2);
  const [complementaryStatus, setComplementaryStatus] = useState(2);
  const [instantStatus, setInstantStatus] = useState(2);
  const currentDate = new Date().toISOString().split("T")[0];

  const bookings = useSelector((state) => state.booking.bookings || []);
  const totalRecord = useSelector((state) => state.booking?.totalRecords || 0);
  const { loading: bookingLoading } = useSelector((state) => state.booking);

  const {
    customers: customerResponse,
    loading: customerLoading,
    error: customerError
  } = useSelector((state) =>
    user?.user_type === 2 ? state.vendors : state.customers
  );

  const {
    vendors: vendorResponse,
    loading: vendorLoading,
    error: vendorError
  } = useSelector((state) => state.vendors);

  const loading =
    type === "customers"
      ? customerLoading
      : type === "vendors"
      ? vendorLoading
      : type === "bookings"
      ? bookingLoading
      : false;

  const error =
    type === "customers"
      ? customerError
      : type === "vendors"
      ? vendorError
      : null;

  const handleBookingTabChange = (event, newType) => {
    setBookingType(newType);
    if (newType !== 4 && newType !== 5) {
      setComplementaryStatus(2);
      setInstantStatus(2);
    }
    dispatch(resetBookingDetails());
  };

  useEffect(() => {
    if (type === "customers") {
      if (user?.user_type === 2) {
        dispatch(
          fetchCustomersByVendorRequest({
            pagination_required: false,
            pagesize: 10,
            pageNo: 1,
            vendor_id: user?.vendor_id,
            ...customFilters
          })
        );
      } else {
        dispatch(fetchAllCustomersRequest(customFilters));
      }
    } else if (type === "vendors") {
      dispatch(
        fetchAllVendorsRequest({
          pagination_required: false,
          pagesize: 10,
          pageNo: 1,
          ...customFilters
        })
      );
    } else if (type === "bookings") {
      let bookingTypeValue = "upcoming";
      if (bookingType === 1) {
        bookingTypeValue = "previous";
      } else if (bookingType === 3) {
        bookingTypeValue = "cancelled";
      }

      const requestParams = {
        pageNo: customFilters.page || 1,
        pagesize: customFilters.rowsPerPage || 10,
        type: bookingTypeValue,
        booked_type: 1,
        currentDate,
        ...customFilters
      };

      if (user.role === "Customer" || user.role === "Vendor") {
        dispatch(
          fetchCustomerBookingsRequest({
            ...requestParams,
            customer_id: user.customer_id || "",
            vendor_id: user.vendor_id || ""
          })
        );
      } else {
        if (bookingType === 4) {
          dispatch(
            fetchComplementaryBookingsRequest({
              ...requestParams,
              approove_status: complementaryStatus
            })
          );
        } else if (bookingType === 5) {
          let instantBookingType;
          switch (instantStatus) {
            case 1:
              instantBookingType = "previous";
              break;
            case 2:
              instantBookingType = "upcoming";
              break;
            case 3:
              instantBookingType = "cancelled";
              break;
            default:
              instantBookingType = "upcoming";
          }

          dispatch(
            fetchInstantBookingsRequest({
              ...requestParams,
              type: instantBookingType
            })
          );
        } else {
          dispatch(fetchBookingsRequest(requestParams));
        }
      }
    }
  }, [
    dispatch,
    type,
    user?.user_type,
    user?.vendor_id,
    bookingType,
    complementaryStatus,
    instantStatus,
    JSON.stringify(customFilters)
  ]);

  useEffect(() => {
    if (customerResponse?.data && type === "customers") {
      const transformedData = customerResponse.data.map((customer) => ({
        name: `${customer.first_name || ""} ${customer.last_name || ""}`.trim(),
        email: customer.email || "-",
        phone: customer.phone || "-",
        address: customer.address1
          ? `${customer.address1}, ${customer.city_name || ""}, ${
              customer.state_name || ""
            }, ${customer.country_name || ""}, ${customer.pincode || ""}`
          : "-",
        lastActive: customer.last_active,
        dateAdded: customer.created_at,
        booking_number: customer.booking_serial_no || "-",
        booking_status: customer.status === 0 ? "Active" : "Inactive"
      }));
      setData(transformedData);
    }
  }, [customerResponse?.data, type]);

  useEffect(() => {
    if (vendorResponse?.data && type === "vendors") {
      const transformedData = vendorResponse.data.map((vendor) => ({
        agencyName: vendor.agency_name || "-",
        contactPerson: `${vendor.first_name || ""} ${
          vendor.last_name || ""
        }`.trim(),
        email: vendor.email || "-",
        phone: vendor.phone || "-",
        address: vendor.address1
          ? `${vendor.address1}, ${vendor.city_name || ""}, ${
              vendor.state_name || ""
            }, ${vendor.country_name || ""}, ${vendor.pincode || ""}`
          : "-",
        registrationNumber: vendor.iata_reg_no || "-",
        gstin: vendor.gstin_number || "-",
        pan: vendor.pan_number || "-",
        lastActive: vendor.last_active,
        createdDate: vendor.created_at,
        booking_status: vendor.status === 0 ? "Active" : "Inactive",
        approvalStatus:
          vendor.approove_status === 1
            ? "Approved"
            : vendor.approove_status === 2
            ? "Pending"
            : "Rejected"
      }));
      setData(transformedData);
    }
  }, [vendorResponse?.data, type]);

  useEffect(() => {
    if (bookings && type === "bookings") {
      const transformedData = bookings.map((item) => {
        const bookingDetail = item.BookingDetail || {};
        const billingAddress = item.BillingAddress || {};
        const order = item.Order || {};
        const numberOfAdults = bookingDetail.number_of_adults || 0;
        const numberOfChildren = bookingDetail.number_of_child || 0;
        const numberOfInfants = bookingDetail.number_of_infant || 0;
        const totalPassengers =
          numberOfAdults + numberOfChildren + numberOfInfants;

        const bookedUser = bookingDetail["BookedUser"] || {};
        let bookedUserName = "-";
        if (bookedUser?.user_type === 3 && bookedUser?.Customer) {
          bookedUserName = bookedUser?.Customer?.full_name;
        } else if (bookedUser?.user_type === 2 && bookedUser?.Vendor) {
          bookedUserName = bookedUser?.Vendor?.full_name;
        } else if (bookedUser?.user_type === 4 && bookedUser?.Staff) {
          bookedUserName = bookedUser?.Staff?.full_name;
        } else {
          bookedUserName =
            `${bookedUser?.first_name || ""} ${
              bookedUser?.last_name || ""
            }`.trim() || "-";
        }

        return {
          booking_number: item.id || "-",
          booking_serial_no: item.booking_serial_no || "-",
          date_of_booking: item.created_at
            ? formatDateWithAmPm(item.created_at)
            : "-",
          booked_user_name: bookedUserName || "-",
          booked_for: item?.Customer?.full_name || "-",
          pnr_number: item?.pnr_number || "-",
          airport_name: item.airport_name || "-",
          airport_type: item.airport_type || "-",
          first_name: billingAddress.first_name || "-",
          last_name: billingAddress.last_name || "-",
          company_name: billingAddress.company_name || "-",
          number_of_adults: numberOfAdults || "-",
          number_of_child: numberOfChildren || "-",
          number_of_infant: numberOfInfants || "-",
          total_passenger: totalPassengers,
          service: item.Service?.services || order.service || "-",
          service_type_label:
            { 1: "Arrival", 2: "Departure", 3: "Transit" }[item.service_type] ||
            "-",
          notes: bookingDetail.notes || "-",
          datetime_of_arrival:
            bookingDetail?.date_of_arrival !== null
              ? `${bookingDetail?.date_of_arrival} ${bookingDetail?.time_of_arrival}`
              : "-",
          datetime_of_departure:
            bookingDetail?.date_of_departure !== null
              ? `${bookingDetail?.date_of_departure} ${bookingDetail?.time_of_departure}`
              : "-",
          arrival_city: bookingDetail.arrival_city || "-",
          arrival_airline: bookingDetail.arrival_airlines || "-",
          arrival_flight_number: bookingDetail.arrival_flight_number || "-",
          departure_city: bookingDetail.departure_city || "-",
          departure_airline: bookingDetail.departure_airlines || "-",
          departure_flight_number: bookingDetail.departure_flight_number || "-",
          payment_status: order.status || "-",
          status: item.status || 0,
          arrival_flight:
            `${bookingDetail.arrival_airlines || ""} ${
              bookingDetail.arrival_flight_number || ""
            }`.trim() || "-",
          departure_flight:
            `${bookingDetail.departure_airlines || ""} ${
              bookingDetail.departure_flight_number || ""
            }`.trim() || "-"
        };
      });
      setData(transformedData);
    }
  }, [bookings, type]);

  if (loading) {
    return (
      <Box textAlign="center" py={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" py={5} color="error.main">
        <p>Error loading data: {error}</p>
      </Box>
    );
  }

  return (
    <div>
      {type === "bookings" && (
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={bookingType}
            onChange={handleBookingTabChange}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Previous" value={1} />
            <Tab label="Upcoming" value={2} />
            <Tab label="Cancelled" value={3} />
            {user.role !== "Customer" && user.role !== "Vendor" && (
              <Tab label="Complementary" value={4} />
            )}
            {user.role !== "Customer" && user.role !== "Vendor" && (
              <Tab label="Instant" value={5} />
            )}
          </Tabs>
        </Box>
      )}

      {type === "bookings" && bookingType === 4 && (
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={complementaryStatus}
            onChange={(e, newStatus) => setComplementaryStatus(newStatus)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Approved" value={1} />
            <Tab label="Pending" value={2} />
            <Tab label="Rejected" value={3} />
          </Tabs>
        </Box>
      )}

      {type === "bookings" && bookingType === 5 && (
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={instantStatus}
            onChange={(e, newStatus) => setInstantStatus(newStatus)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Previous" value={1} />
            <Tab label="Upcoming" value={2} />
            <Tab label="Cancelled" value={3} />
          </Tabs>
        </Box>
      )}

      <div className="report-table-container">
        <table>
          <thead>
            <tr>
              {type === "customers" && (
                <>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Address</th>
                  <th>Last Active</th>
                  <th>Created Date</th>
                  <th>Status</th>
                </>
              )}
              {type === "vendors" && (
                <>
                  <th>Agency Name</th>
                  <th>Vendor Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Address</th>
                  <th>Registration Number</th>
                  <th>GSTIN Number</th>
                  <th>PAN Number</th>
                  <th>Last Active</th>
                  <th>Created Date</th>
                  <th>Status</th>
                  <th>Approval Status</th>
                </>
              )}
              {type === "bookings" && (
                <>
                  <th>Booking Number</th>
                  <th>Booking Date</th>
                  <th>Booked By</th>
                  <th>Booked For</th>
                  <th>Service</th>
                  <th>Service Type</th>
                  <th>No. Adults</th>
                  <th>No. Children</th>
                  <th>No. Infants</th>
                  <th>PNR Number</th>
                  <th>Arrival Date/Time</th>
                  <th>Arrival Flight</th>
                  <th>Departure Date/Time</th>
                  <th>Departure Flight</th>
                  <th>Payment Status</th>
                </>
              )}
              {type === "leads" && (
                <>
                  <th>Lead ID</th>
                  <th>Source</th>
                  <th>Enquiry Date</th>
                  <th>Enquiry Time</th>
                  <th>Status</th>
                  <th>Cost</th>
                  <th>Profit</th>
                  <th>Follow Up</th>
                  <th>Payment Mode</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? (
              data.map((item, index) => (
                <tr key={index}>
                  {type === "customers" && (
                    <>
                      <td>{item.name}</td>
                      <td>{item.email}</td>
                      <td>{item.phone}</td>
                      <td>{item.address}</td>
                      <td>{formatDateWithAmPm(item.lastActive)}</td>
                      <td>{formatDateWithAmPm(item.dateAdded)}</td>
                      <td>
                        <span
                          className={`report-badge ${getStatusClass(
                            item.booking_status
                          )}`}
                        >
                          {item.booking_status}
                        </span>
                      </td>
                    </>
                  )}
                  {type === "vendors" && (
                    <>
                      <td>{item.agencyName}</td>
                      <td>{item.contactPerson}</td>
                      <td>{item.email}</td>
                      <td>{item.phone}</td>
                      <td>{item.address}</td>
                      <td>{item.registrationNumber}</td>
                      <td>{item.gstin}</td>
                      <td>{item.pan}</td>
                      <td>{formatDateWithAmPm(item.lastActive)}</td>
                      <td>{formatDateWithAmPm(item.createdDate)}</td>
                      <td>
                        <span
                          className={`report-badge ${getStatusClass(
                            item.booking_status
                          )}`}
                        >
                          {item.booking_status}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge ${getStatusClass(
                            item.approvalStatus
                          )}`}
                        >
                          {item.approvalStatus}
                        </span>
                      </td>
                    </>
                  )}
                  {type === "bookings" && (
                    <>
                      <td>{item.booking_serial_no}</td>
                      <td>{item.date_of_booking}</td>
                      <td>{item.booked_user_name}</td>
                      <td>{item.booked_for}</td>
                      <td>{item.service}</td>
                      <td>{item.service_type_label}</td>
                      <td>{item.number_of_adults}</td>
                      <td>{item.number_of_child}</td>
                      <td>{item.number_of_infant}</td>
                      <td>{item.pnr_number}</td>
                      <td>{item.datetime_of_arrival}</td>
                      <td>{item.arrival_flight}</td>
                      <td>{item.datetime_of_departure}</td>
                      <td>{item.departure_flight}</td>
                      <td>
                        <span
                          className={`report-badge ${getStatusClass(
                            item.payment_status
                          )}`}
                        >
                          {item.payment_status}
                        </span>
                      </td>
                    </>
                  )}
                  {type === "leads" && (
                    <>
                      <td>{item.leadId}</td>
                      <td>{item.source}</td>
                      <td>{formatDateWithAmPm(item.enquiryDate)}</td>
                      <td>{item.enquiryTime}</td>
                      <td>
                        <span
                          className={`badge ${getStatusClass(item.status)}`}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td>{item.cost}</td>
                      <td>{item.profit}</td>
                      <td>{formatDateWithAmPm(item.followUpDate)}</td>
                      <td>{item.modeOfPayment}</td>
                    </>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={
                    type === "customers"
                      ? 8
                      : type === "vendors"
                      ? 11
                      : type === "bookings"
                      ? 15
                      : 9
                  }
                  style={{ textAlign: "center" }}
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReportTable;
